import { useMutation } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Input, Modal, ModalCloseButton } from 'components/Shared';
import { MODAL_DISMISS_ACTION } from 'constants/Common';
import { useOrder } from 'hooks/api/scheduling/useOrder';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';
import * as S from './styles';

export interface Ref {
  openModal: () => void;
}

interface Props {
  ids: number[];
  onUpdate?: (item: any) => void;
}

interface ICancelItemModal
  extends ForwardRefExoticComponent<Props & RefAttributes<Ref>> {}

const CancelItemModal: ICancelItemModal = forwardRef((props, ref) => {
  const { ids, onUpdate } = props;
  const formRef = useRef<FormHandles>(null);
  const [isOpen, setIsOpen] = useState(false);

  const { cancelOrderItems } = useOrder();

  const { mutate: itemMutation, isPending: isUpdating } = useMutation({
    mutationFn: (items: any) => cancelOrderItems(items),
    onSuccess: (data: any) => {
      toast('Itens cancelados com sucesso');
      onModalClose();
      onUpdate && onUpdate(data);
    },
    onError: (error: any) => {
      toast('Erro ao cancelar itens', {
        description: error.response.data.message,
      });
    },
  });

  const handleSubmit = useCallback((): void => {
    const reason = formRef.current?.getFieldValue('reason');

    if (!reason) {
      formRef.current?.setFieldError('reason', 'Campo obrigatório');
      return;
    } else {
      formRef.current?.setFieldError('reason', '');
    }

    const payload = {
      ids,
      reason,
    };

    itemMutation(payload);
  }, [ids, itemMutation]);

  const onModalClose = useCallback((): void => {
    setIsOpen(false);
    formRef.current?.setErrors({});
    formRef.current?.reset();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      openModal: (): void => {
        setIsOpen(true);
      },
    }),
    []
  );

  return (
    <Modal isOpen={isOpen} onClickOutside={onModalClose}>
      <S.ModalContent style={{ maxWidth: '720px' }}>
        <S.ModalHeader>
          <h4>Cancelar itens</h4>
          <ModalCloseButton onClick={onModalClose} />
        </S.ModalHeader>
        <S.ModalBody>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <S.FormRow>
              <Input name="reason" label="Justificativa" />
            </S.FormRow>
            <S.FormActions>
              <S.Button type="button" mood="light" onClick={onModalClose}>
                {MODAL_DISMISS_ACTION}
              </S.Button>
              <S.Button type="submit">
                {isUpdating ? <S.ActivityIndicator /> : 'Salvar'}
              </S.Button>
            </S.FormActions>
          </Form>
        </S.ModalBody>
      </S.ModalContent>
    </Modal>
  );
});

export default CancelItemModal;
